import React, { useEffect, useState } from "react";
import {
  List,
  Eye,
  Package,
  CheckCircle,
  Clock,
  RefreshCw,
  Download,
  Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  generateColumnsFromData,
  generateRowsWithIds,
} from "../../utils/constants";
import useApi from "../../hooks/useApi";
import checkIfUserHasAccess from "../common/UserCheckAccess";
import InvoiceDataGrid from "../invoiceList/InvoiceDataGrid";
import InvoiceActivityLogs from "../invoiceList/InvoiceActivityLogs";
import useDocumentList from "../../hooks/useDocumentList";

const ProductionDashboard = () => {
  const [productionFilter, setProductionFilter] = useState("wo_pending");
  const [p1Value, setP1Value] = useState("ALL");
  const [modalType, setModalType] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  const navigate = useNavigate();
  const { request } = useApi();

  const getSelectedDocNumber = (row) => {
    if (!row) return null;
    return (
      row.woNo ||
      row.Wo_No ||
      row.WO_NO ||
      row.prodNo ||
      row.PROD_NO ||
      row.Prod_No ||
      row.id ||
      null
    );
  };
  const handleMakeProduction = () => {
    if (productionFilter !== "wo_pending") {
      alert("Please switch to WO PENDING to make production.");
      return;
    }

    const docNo = getSelectedDocNumber(selectedRowData);
    if (!docNo) {
      alert("Please select a PENDING item from the Production Table.");
      return;
    }

    const checkProductAndNavigate = async () => {
      const productCode = selectedRowData?.Product_Code;
      if (!productCode) {
        alert("Product_Code not found in selected row.");
        return;
      }
      try {
        const resp = await request({
          url: `${config.BASE_URL}/api/cpayscount/${config.COMPANY_CODE}/ISPRODUCTITEM/${encodeURIComponent(productCode)}/-`,
          method: "GET",
        });
        const cnt = Number(resp?.[0]?.CNT ?? 0);
        if (cnt === 0) {
          const workOrder = selectedRowData;
          navigate("/production", { state: { workOrder } });
        } else {
          alert("Product is not valid for production.");
        }
      } catch (err) {
        alert("Failed to check product validity.");
      }
    };
    checkProductAndNavigate();
  };
  const handleFilterChange = (filter) => {
    setProductionFilter(filter);
    setSelectionModel([]);
    setSelectedRowData({});
    setPageNumber(1);
  };
  const {
    config,
    searchTerm,
    setSearchTerm,
    searchType,
    setSearchType,
    showSidebar,
    setShowSidebar,
    columnVisibilityModel,
    setColumnVisibilityModel,
    documentData,
    selectionModel,
    setSelectionModel,
    selectedRowData,
    setSelectedRowData,
    loading,
    handleEdit,
    exportDocumentList,
    handleView,
    pageNumber,
    setPageNumber,
    currentEnd,
    currentStart,
    totalCount,
    refreshDocumentList,
    columns,
    rows,
  } = useDocumentList({
    docType:
      productionFilter === "wo_pending" ? "PRODUCTIONPEND" : productionFilter === "in_progress" ? "PRODUCTIONUNCOSTED" : "PRODUCTIONCOMPLETED",
    p1Value,
    p2Value: "-",
    docNoField: productionFilter === "wo_pending" ? "Wo_No" : "PROD_NO",
    editRoute: "/production",
    screenCode: "",
    initialSearchType: "-",
  });
  console.log("selectedRowData", selectedRowData);
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const pend = await request({
          url: `${config.BASE_URL}/api/CpaysCount/${config.COMPANY_CODE
            }/PRODPENDCOUNT/${config.DEPARTMENT_NO || "MAIN"}/-`,
          method: "GET",
        });
        const prog = await request({
          url: `${config.BASE_URL}/api/CpaysCount/${config.COMPANY_CODE
            }/PRODUNCOSTEDCOUNT/${config.DEPARTMENT_NO || "MAIN"}/-`,
          method: "GET",
        });
        const comp = await request({
          url: `${config.BASE_URL}/api/CpaysCount/${config.COMPANY_CODE
            }/PRODCOMPLCOUNT/${config.DEPARTMENT_NO || "MAIN"}/-`,
          method: "GET",
        });

        const extract = (resp) =>
          Number(resp?.[0]?.CNT ?? resp?.[0]?.Column1 ?? resp ?? 0);

        setPendingCount(extract(pend));
        setInProgressCount(extract(prog));
        setCompletedCount(extract(comp));
      } catch (err) {
        console.error("Failed to fetch production counts:", err);
        setPendingCount(0);
        setInProgressCount(0);
        setCompletedCount(0);
      }
    };

    fetchCounts();
  }, [productionFilter]);

  useEffect(() => {
    refreshDocumentList();
  }, [productionFilter]);

  const handleExport = () => {
    exportDocumentList();
  };

  return (
    <div className="bg-gray-50 p-3">
      <div className="max-w-full mx-auto py-0 space-y-1">
        {/* Compact Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2">
          <div className="flex items-center justify-between gap-3 w-full">
            <div className=" flex items-center">
              {/* Title */}
              <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">
                Production Dashboard
              </h1>

              {/* Status Filters */}
              <div className="flex gap-2">
                {/* WO Pending Tab */}
                <button
                  onClick={() => handleFilterChange("wo_pending")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${productionFilter === "wo_pending"
                    ? "bg-orange-500 text-white shadow-sm"
                    : "bg-orange-50 text-orange-600 hover:bg-orange-100"
                    }`}
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span>WO Pending</span>
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] transition-all ${productionFilter === "wo_pending"
                      ? "bg-white/20 text-white"
                      : "bg-orange-200/50 text-orange-700"
                      }`}
                  >
                    {pendingCount}
                  </span>
                </button>

                {/* Un Costed Tab */}
                <button
                  onClick={() => handleFilterChange("in_progress")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${productionFilter === "in_progress"
                    ? "bg-blue-500 text-white shadow-sm"
                    : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                    }`}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Un Costed</span>
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] transition-all ${productionFilter === "in_progress"
                      ? "bg-white/20 text-white"
                      : "bg-blue-200/60 text-blue-700"
                      }`}
                  >
                    {inProgressCount}
                  </span>
                </button>

                {/* Completed Tab */}
                <button
                  onClick={() => handleFilterChange("completed")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${productionFilter === "completed"
                    ? "bg-green-500 text-white shadow-sm"
                    : "bg-green-50 text-green-600 hover:bg-green-100"
                    }`}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>Completed</span>
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] transition-all ${productionFilter === "completed"
                      ? "bg-white/20 text-white"
                      : "bg-green-200/60 text-green-700"
                      }`}
                  >
                    {completedCount}
                  </span>
                </button>
              </div>
            </div>
            <div className=" flex items-center justify-end gap-2">
              {/* Search */}
              <div className="relative flex-1 max-w-sm ml-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search work orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {productionFilter === "wo_pending" && (
                  <button
                    onClick={handleMakeProduction}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all"
                  >
                    Make Production
                  </button>
                )}

                <button
                  onClick={handleExport}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {showSidebar && (
            <InvoiceActivityLogs
              invoiceId={selectedRowData?.prodNo || selectedRowData?.woNo}
              COMPANY_CODE={config.COMPANY_CODE}
              BASE_URL={config.BASE_URL}
              modificationType={"PRODUCTIONTIMELINE"}
              printType={"PRODUCTIONPRINTTIMELINE"}
              DEPARTMENT={config.DEPARTMENT_NO}
            />
          )}
          <div
            className={`col-span-12 ${showSidebar ? "lg:col-span-9" : "lg:col-span-12"
              } transition-all duration-300`}
          >
            <InvoiceDataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              selectionModel={selectionModel}
              setSelectionModel={setSelectionModel}
              setSelectedRowData={setSelectedRowData}
              selectedRowData={selectedRowData}
              onEdit={handleEdit}
              onView={handleView}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              currentStart={currentStart}
              currentEnd={currentEnd}
              totalCount={totalCount}
              showSidebar={showSidebar}
              docLabel={productionFilter === "wo_pending" ? "Wo_No" : "PROD_NO"}
              enableF5Action={false}
              config={config}
              refreshDocumentList={refreshDocumentList}
              modalType={"close"}
              setModalType={setModalType}
              f5Mode={"POSETTLEMENT"}
              showApproveReject={false}
              reviewMode="PURCHASEORDER"
              showEdit={productionFilter !== "wo_pending"}
              showUpdateButton={true}
              showView={productionFilter !== "wo_pending"}

            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionDashboard;
